import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MobileNavigation } from '../components/MobileNavigation';
import { FileText, Copy, EnvelopeSimple } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
import {
  SessionForm,
  BulkSessionForm,
  EditSessionDialog,
  SessionRosterDialog,
  SessionList,
  WaitlistDialog,
  SessionPhotosDialog,
  getDefaultSessionFormData,
  getDefaultBulkFormData
} from '../components/sessions';

/**
 * AdminSessions - Admin page for managing basketball sessions
 * Refactored to use modular components from /components/sessions/
 */
export function AdminSessions() {
  const location = useLocation();
  
  // Core data state
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Dialog visibility states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRosterDialog, setShowRosterDialog] = useState(false);
  const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  
  // Roster state
  const [rosterSession, setRosterSession] = useState(null);
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  
  // Waitlist state
  const [waitlistSession, setWaitlistSession] = useState(null);
  
  // Copy/Message state
  const [copySession, setCopySession] = useState(null);
  const [copyDate, setCopyDate] = useState('');
  const [messageSession, setMessageSession] = useState(null);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Photos state
  const [photosSession, setPhotosSession] = useState(null);
  const [showPhotosDialog, setShowPhotosDialog] = useState(false);
  
  // Action states
  const [cancellingSession, setCancellingSession] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  
  // Form data states
  const [formData, setFormData] = useState(getDefaultSessionFormData());
  const [editFormData, setEditFormData] = useState(getDefaultSessionFormData());
  const [bulkFormData, setBulkFormData] = useState(getDefaultBulkFormData());
  
  // Filter states
  const [showDraftsOnly, setShowDraftsOnly] = useState(false);
  
  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);
  
  // Reload when navigating to this page
  useEffect(() => {
    loadData();
  }, [location.key]);
  
  // Reload when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
  
  // ==================== API FUNCTIONS ====================
  
  const loadData = async () => {
    try {
      // Load sessions - admin sees all including drafts
      const sessionsResponse = await fetch(`${getApiBaseUrl()}/api/sessions?include_drafts=true`, {
        credentials: 'include'
      });
      if (sessionsResponse.ok) {
        const sessionsData = await sessionsResponse.json();
        setSessions(sessionsData);
      }
      
      // Load locations - public endpoint, no credentials needed
      const locationsResponse = await fetch(`${getApiBaseUrl()}/api/locations`);
      if (locationsResponse.ok) {
        const locationsData = await locationsResponse.json();
        setLocations(locationsData);
      }
      
    } catch (error) {
      console.error('Load data error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.location_id) {
      toast.error('Please select a location');
      return;
    }
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const newSession = await response.json();
        setSessions([newSession, ...sessions]);
        setFormData(getDefaultSessionFormData());
        setShowAddDialog(false);
        toast.success('Session created successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create session');
      }
    } catch (error) {
      console.error('Create session error:', error);
      toast.error('Failed to create session');
    }
  };
  
  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/sessions/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(bulkFormData)
      });
      
      if (response.ok) {
        const result = await response.json();
        loadData();
        setShowBulkDialog(false);
        toast.success(`Created ${result.sessions.length} sessions!`);
        setBulkFormData(getDefaultBulkFormData());
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create sessions');
      }
    } catch (error) {
      console.error('Bulk create error:', error);
      toast.error('Failed to create sessions');
    }
  };
  
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/sessions/${editingSession.session_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editFormData)
      });
      
      if (response.ok) {
        toast.success('Session updated successfully');
        setShowEditDialog(false);
        setEditingSession(null);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update session');
      }
    } catch (error) {
      console.error('Edit session error:', error);
      toast.error('Failed to update session');
    }
  };
  
  // ==================== EVENT HANDLERS ====================
  
  const openEditDialog = (session) => {
    setEditingSession(session);
    setEditFormData({
      location_id: session.location_id || '',
      title: session.title || '',
      date: session.date || '',
      start_time: session.start_time || '',
      end_time: session.end_time || '',
      court: session.court || '',
      capacity: session.capacity || 25,
      price: session.price !== undefined ? session.price : 15.0,
      grade_group: session.grade_group || 'elementary'
    });
    setShowEditDialog(true);
  };
  
  const handleViewRoster = async (session) => {
    setRosterSession(session);
    setShowRosterDialog(true);
    setRosterLoading(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/sessions/${session.session_id}/roster`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setRoster(Array.isArray(data) ? data : (data.roster || []));
      } else {
        toast.error('Failed to load roster');
        setRoster([]);
      }
    } catch (error) {
      console.error('Load roster error:', error);
      toast.error('Failed to load roster');
      setRoster([]);
    } finally {
      setRosterLoading(false);
    }
  };
  
  const handleViewWaitlist = (session) => {
    setWaitlistSession(session);
    setShowWaitlistDialog(true);
  };
  
  const handleViewPhotos = (session) => {
    setPhotosSession(session);
    setShowPhotosDialog(true);
  };
  
  const handleCopySession = (session) => {
    setCopySession(session);
    setCopyDate('');
    setShowCopyDialog(true);
  };
  
  const handleCopySubmit = async () => {
    if (!copyDate) {
      toast.error('Please select a date');
      return;
    }
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/sessions/${copySession.session_id}/copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ date: copyDate })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowCopyDialog(false);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to copy session');
      }
    } catch (error) {
      console.error('Copy session error:', error);
      toast.error('Failed to copy session');
    }
  };
  
  const handleMessageSession = (session) => {
    setMessageSession(session);
    setMessageSubject(`Update: ${session.title || session.date} Session`);
    setMessageBody('');
    setShowMessageDialog(true);
  };
  
  const handleSendMessage = async () => {
    if (!messageSubject || !messageBody) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    setSendingMessage(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/sessions/${messageSession.session_id}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          subject: messageSubject, 
          message: messageBody 
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowMessageDialog(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Send message error:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };
  
  const handleDeleteSession = async (sessionId, isCancelled = false) => {
    const message = isCancelled 
      ? 'This cancelled session has registrations. Delete session AND all its registrations? This cannot be undone.'
      : 'Are you sure you want to delete this session? This cannot be undone.';
    
    if (!window.confirm(message)) return;
    
    try {
      const url = isCancelled 
        ? `${getApiBaseUrl()}/api/admin/sessions/${sessionId}?force=true`
        : `${getApiBaseUrl()}/api/admin/sessions/${sessionId}`;
        
      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        const regCount = result.registrations_deleted || 0;
        toast.success(regCount > 0 
          ? `Session deleted with ${regCount} registration(s)` 
          : 'Session deleted successfully');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete session');
      }
    } catch (error) {
      console.error('Delete session error:', error);
      toast.error('Failed to delete session');
    }
  };
  
  const handleCancelSession = async (session) => {
    const confirmMessage = session.registered_count > 0
      ? `Are you sure you want to cancel this session?\n\n${session.registered_count} player(s) will receive account credits and email notifications.`
      : 'Are you sure you want to cancel this session?';
    
    if (!window.confirm(confirmMessage)) return;
    
    setCancellingSession(session.session_id);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/sessions/${session.session_id}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Session cancelled. ${result.credits_issued?.length || 0} credits issued.`);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel session');
      }
    } catch (error) {
      console.error('Cancel session error:', error);
      toast.error('Failed to cancel session');
    } finally {
      setCancellingSession(null);
    }
  };
  
  const handleRestoreSession = async (session) => {
    if (!window.confirm('Are you sure you want to restore this session? Note: Previous registrations will not be automatically restored.')) {
      return;
    }
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/sessions/${session.session_id}/restore`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Session restored successfully');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to restore session');
      }
    } catch (error) {
      console.error('Restore session error:', error);
      toast.error('Failed to restore session');
    }
  };
  
  const handlePublishSession = async (session) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/sessions/${session.session_id}/publish`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Session published! Now visible to parents.');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to publish session');
      }
    } catch (error) {
      console.error('Publish session error:', error);
      toast.error('Failed to publish session');
    }
  };
  
  const handleUnpublishSession = async (session) => {
    if (!window.confirm('Move this session to drafts? It will be hidden from parents.')) return;
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/sessions/${session.session_id}/unpublish`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Session moved to drafts');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to unpublish session');
      }
    } catch (error) {
      console.error('Unpublish session error:', error);
      toast.error('Failed to unpublish session');
    }
  };
  
  const handleEndSession = async (session) => {
    if (!window.confirm('End this session? Registration will be closed but the session will remain visible.')) return;
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/sessions/${session.session_id}/end`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Session marked as ended');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to end session');
      }
    } catch (error) {
      console.error('End session error:', error);
      toast.error('Failed to end session');
    }
  };
  
  const handleReopenSession = async (session) => {
    if (!window.confirm('Reopen this session? Registration will be allowed again.')) return;
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/sessions/${session.session_id}/reopen`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Session reopened');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reopen session');
      }
    } catch (error) {
      console.error('Reopen session error:', error);
      toast.error('Failed to reopen session');
    }
  };
  
  const exportRosterCSV = async (sessionId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/sessions/${sessionId}/roster/csv`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `roster_${sessionId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Roster exported!');
      } else {
        toast.error('Failed to export roster');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export roster');
    }
  };
  
  // ==================== RENDER ====================
  
  // Calculate draft count for display
  const draftCount = sessions.filter(s => s.published === false).length;
  const filteredSessions = showDraftsOnly 
    ? sessions.filter(s => s.published === false)
    : sessions;
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-24" data-testid="admin-sessions">
      {/* Premium Header */}
      <div className="glass-effect sticky top-0 z-40">
        <div className="px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Manage</p>
              <h1 className="text-2xl font-black uppercase tracking-tight">
                {filteredSessions.length} Session{filteredSessions.length !== 1 ? 's' : ''}
              </h1>
              {draftCount > 0 && !showDraftsOnly && (
                <p className="text-sm text-yellow-500 mt-1">
                  {draftCount} draft{draftCount !== 1 ? 's' : ''} pending publication
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Draft Filter Toggle */}
              <Button 
                variant={showDraftsOnly ? "default" : "outline"} 
                className={`flex-shrink-0 ${showDraftsOnly ? 'bg-yellow-500 hover:bg-yellow-600' : 'hover:border-yellow-500/50'}`}
                onClick={() => setShowDraftsOnly(!showDraftsOnly)}
                data-testid="toggle-drafts-btn"
              >
                <FileText size={18} weight="bold" className="mr-2" />
                {showDraftsOnly ? 'Show All' : 'Drafts'}
              </Button>
              
              {/* Bulk Create Dialog */}
              <BulkSessionForm
                open={showBulkDialog}
                onOpenChange={setShowBulkDialog}
                formData={bulkFormData}
                setFormData={setBulkFormData}
                locations={locations}
                onSubmit={handleBulkSubmit}
              />
              
              {/* Add Session Dialog */}
              <SessionForm
                open={showAddDialog}
                onOpenChange={setShowAddDialog}
                formData={formData}
                setFormData={setFormData}
                locations={locations}
                onSubmit={handleSubmit}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Sessions List */}
      <div className="px-4 py-6">
        <SessionList
          sessions={sessions}
          locations={locations}
          showDraftsOnly={showDraftsOnly}
          onViewRoster={handleViewRoster}
          onViewWaitlist={handleViewWaitlist}
          onEdit={openEditDialog}
          onDelete={handleDeleteSession}
          onCancel={handleCancelSession}
          onRestore={handleRestoreSession}
          onPublish={handlePublishSession}
          onUnpublish={handleUnpublishSession}
          onExportCSV={exportRosterCSV}
          onCopy={handleCopySession}
          onMessage={handleMessageSession}
          onViewPhotos={handleViewPhotos}
          onEndSession={handleEndSession}
          onReopenSession={handleReopenSession}
          onAddSession={() => setShowAddDialog(true)}
          cancellingSession={cancellingSession}
        />
      </div>
      
      {/* Edit Session Dialog */}
      <EditSessionDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        formData={editFormData}
        setFormData={setEditFormData}
        locations={locations}
        onSubmit={handleEditSubmit}
      />
      
      {/* View Roster Dialog */}
      <SessionRosterDialog
        open={showRosterDialog}
        onOpenChange={setShowRosterDialog}
        session={rosterSession}
        roster={roster}
        loading={rosterLoading}
        onRefresh={() => {
          // Reload roster data
          if (rosterSession) {
            setRosterLoading(true);
            fetch(`${getApiBaseUrl()}/api/sessions/${rosterSession.session_id}/roster`, {
              credentials: 'include'
            })
              .then(res => res.json())
              .then(data => setRoster(Array.isArray(data) ? data : (data.roster || [])))
              .catch(err => console.error('Failed to refresh roster:', err))
              .finally(() => setRosterLoading(false));
          }
          // Also reload sessions to update counts
          loadData();
        }}
      />
      
      {/* Waitlist Dialog */}
      <WaitlistDialog
        open={showWaitlistDialog}
        onOpenChange={setShowWaitlistDialog}
        session={waitlistSession}
      />
      
      {/* Photos Dialog */}
      <SessionPhotosDialog
        open={showPhotosDialog}
        onOpenChange={setShowPhotosDialog}
        session={photosSession}
      />
      
      {/* Copy Session Dialog */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Copy Session</DialogTitle>
            <DialogDescription>
              Create a copy of this session with a new date. The copy will be saved as a draft.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Original Session</Label>
              <p className="text-sm text-muted-foreground">
                {copySession?.title || copySession?.date} • {copySession?.start_time}
              </p>
            </div>
            <div>
              <Label htmlFor="copy-date">New Date</Label>
              <Input
                id="copy-date"
                type="date"
                value={copyDate}
                onChange={(e) => setCopyDate(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopyDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCopySubmit} className="btn-primary">
              <Copy size={16} className="mr-2" />
              Create Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Message Parents Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Message Parents</DialogTitle>
            <DialogDescription>
              Send an email to all {messageSession?.registered_count} parent(s) registered for this session.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="message-subject">Subject</Label>
              <Input
                id="message-subject"
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
                placeholder="Enter email subject..."
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="message-body">Message</Label>
              <Textarea
                id="message-body"
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                placeholder="Enter your message to parents..."
                className="mt-1 min-h-[150px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendMessage} disabled={sendingMessage} className="btn-primary">
              {sendingMessage ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <EnvelopeSimple size={16} className="mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <MobileNavigation userRole="admin" />
    </div>
  );
}
